import { hashString } from '@framework/data-helpers/string/hashString';

interface ICacheOptions {
  clearCache?: boolean,
  cacheError?: boolean,
}

function isCacheOptions(value: unknown): value is ICacheOptions {
  return (
    value != null &&
    typeof value === 'object' &&
    ('clearCache' in value || 'cacheError' in value)
  );
}

/**
 * Memoize async function result to promise (helps to resolve duplicate queries).
 * To force request pass { clearCache: true } as the last argument.
 *
 * Cache key is built from all arguments except the options object.
 * The last argument is treated as options ONLY if it contains `clearCache` or `cacheError` keys.
 */
export function withCache(_: {}, key: string, descriptor: PropertyDescriptor): PropertyDescriptor {
  if (typeof descriptor?.value !== 'function') {
    return descriptor;
  }

  const originalFn = descriptor.value;

  descriptor.value = function decorate(this: Record<string, unknown>, ...args: unknown[]) {
    const lastArg = args[args.length - 1];
    const hasOptions = isCacheOptions(lastArg);

    const cacheKeyArgs = hasOptions ? args.slice(0, -1) : args;
    const clearCache = hasOptions ? lastArg.clearCache : false;
    const cacheError = hasOptions ? lastArg.cacheError : false;

    const memoizedStorageKey = `${ key }.${ hashString(JSON.stringify(cacheKeyArgs)) }`;

    this[memoizedStorageKey] = this[memoizedStorageKey] || null;

    if (clearCache) {
      this[memoizedStorageKey] = null;
    }
    if (this[memoizedStorageKey]) {
      return this[memoizedStorageKey];
    }

    // eslint-disable-next-line no-async-promise-executor
    this[memoizedStorageKey] = new Promise(async (resolve, reject) => {
      try {
        const result = await originalFn.apply(this, args);
        return resolve(result);
      } catch (e) {
        if (!cacheError) {
          this[memoizedStorageKey] = null;
        }
        return reject(e);
      }
    });

    return this[memoizedStorageKey];
  };

  return descriptor;
}
